import type React from 'react';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { fadeIn } from '../../utils/animations';
import Button from './Button';

interface ContactFormProps {
  className?: string;
  compact?: boolean;
  submitLabel?: string;
  successMessage?: string;
  withComment?: boolean;
  light?: boolean;
}

const ContactForm: React.FC<ContactFormProps> = ({
  className = '',
  compact = false,
  submitLabel = 'Отправить заявку',
  successMessage = 'Спасибо за вашу заявку! Наш специалист свяжется с вами в течение 15 минут.',
  withComment = true,
  light = false,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    comment: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Введите ваше имя';
    }

    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = 'Введите ваш телефон';
    } else if (!/^(\+7|8)[- ]?\(?[0-9]{3}\)?[- ]?[0-9]{3}[- ]?[0-9]{2}[- ]?[0-9]{2}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Введите корректный номер телефона';
    }

    // Email validation (optional)
    if (formData.email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Введите корректный email';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field when the user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);

    const browserInfo = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
    };

    const payload = {
      ...formData,
      browserInfo,
    };

    try {
      await fetch('https://kot.internal.haninetwork.com/webhook/beef6f3d-1009-4d5c-8b35-bbb8715a1376', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormData({
        name: '',
        phone: '',
        email: '',
        comment: '',
      });
    } catch (error) {
      console.error('Error sending form data:', error);
      setIsSubmitting(false);
    }
  };

  // Modified form design with modern styling
  const formBgClass = light
    ? 'bg-primary/80 backdrop-blur-md text-white'
    : 'bg-white shadow-xl';

  const inputBaseClasses = "w-full rounded-md border border-transparent bg-opacity-20 px-4 py-3 transition-all duration-300 focus:ring-2 focus:ring-opacity-50 placeholder:text-opacity-60";

  const inputClasses = light
    ? `${inputBaseClasses} bg-white/10 text-white placeholder:text-white/60 focus:border-secondary focus:ring-secondary/30`
    : `${inputBaseClasses} bg-gray-100 text-gray-900 placeholder:text-gray-500 focus:border-primary focus:ring-primary/20`;

  const textareaClasses = `${inputClasses} resize-none min-h-[120px]`;

  const buttonClasses = light
    ? 'bg-secondary text-primary hover:bg-secondary/90 w-full py-3 font-semibold rounded-md transition-all duration-300'
    : 'bg-primary text-white hover:bg-primary/90 w-full py-3 font-semibold rounded-md transition-all duration-300';

  const errorClasses = light
    ? 'mt-1 text-sm text-secondary/90'
    : 'mt-1 text-sm text-red-500';

  const descriptionText = compact
    ? successMessage
    : '';

  return (
    <motion.div className={`${className} overflow-hidden rounded-lg ${formBgClass} p-6 relative`} variants={fadeIn}>
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-40 h-40 bg-secondary/10 rounded-full -mr-20 -mt-20 z-0" />
      <div className="absolute bottom-0 left-0 w-28 h-28 bg-secondary/10 rounded-full -ml-14 -mb-14 z-0" />

      {!isSubmitted ? (
        <div className="relative z-10">
          {!compact && (
            <p className={`mb-6 ${light ? 'text-gray-200' : 'text-gray-600'}`}>
              {descriptionText}
            </p>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="group relative">
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={inputClasses}
                placeholder="Ваше имя *"
                required
              />
              {errors.name && <p className={errorClasses}>{errors.name}</p>}
            </div>

            <div className="group relative">
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={inputClasses}
                placeholder="Ваш телефон *"
                required
              />
              {errors.phone && <p className={errorClasses}>{errors.phone}</p>}
            </div>

            {!compact && (
              <div className="group relative">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={inputClasses}
                  placeholder="Ваш email"
                />
                {errors.email && <p className={errorClasses}>{errors.email}</p>}
              </div>
            )}

            {withComment && !compact && (
              <div className="group relative">
                <textarea
                  id="comment"
                  name="comment"
                  value={formData.comment}
                  onChange={handleChange}
                  className={textareaClasses}
                  placeholder="Кратко опишите вашу задачу"
                  rows={4}
                />
              </div>
            )}

            <div className="pt-2">
              <button
                type="submit"
                className={buttonClasses}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Отправка...
                  </span>
                ) : (
                  submitLabel
                )}
              </button>
            </div>

            <p className={`text-xs ${light ? 'text-white/70' : 'text-gray-500'}`}>
              Нажимая кнопку, вы соглашаетесь с{' '}
              <a href="/privacy-policy" className="underline hover:text-secondary transition-colors">
                условиями обработки персональных данных
              </a>
            </p>
          </form>
        </div>
      ) : (
        <div className="text-center py-8 px-4 relative z-10">
          <div className="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full bg-secondary/20">
            <svg className="w-8 h-8 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className={`text-xl font-semibold ${light ? 'text-white' : 'text-gray-900'} mb-2`}>
            Заявка отправлена!
          </h3>
          <p className={`mb-6 ${light ? 'text-white/90' : 'text-gray-600'}`}>
            {successMessage}
          </p>
          <button
            className={`px-6 py-2 rounded-md ${light ? 'bg-secondary text-primary' : 'bg-primary text-white'} hover:opacity-90 transition-opacity`}
            onClick={() => setIsSubmitted(false)}
          >
            Отправить еще одну заявку
          </button>
        </div>
      )}
    </motion.div>
  );
};

export default ContactForm;
